import {
  type CanvasElementFrom,
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type VisibilityElementProps,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import {
  type BorderRadiusValue,
  type CanvasElementPropertiesWithData,
  type ElementLabelPropsWithPositionAndWidth,
  type ElementLabelStyleProps,
  type ElementSizingMode,
  type PropertiesConfig,
  type PropertiesConfigInputColorModel,
} from '@blaze-shared/elements-domain';
import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';

import { registerElementDefinition } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { AccountsDropdownComponent } from './accounts-dropdown.component';
import { AccountsDropdownPropsBuilder } from './accounts-dropdown.props-builder';
import { accountsDropdownV2Migration } from './migrations/002-accounts-dropdown.migration';

export type AccountsDropdownElement = CanvasElementFrom<'accountsDropdown'>;
export type AccountsDropdownDef = WithProps<AccountsDropdownPropsModel> & WithIoConfig;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    accountsDropdown: AccountsDropdownDef;
  }
}

const DEFAULT_WIDTH = 300;
const MIN_SIZE = 48;
const MAX_WIDTH = 900;
const MAX_HEIGHT = 150;
const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];
const HEIGHT_OPTIONS: ElementSizingMode[] = ['fixed'];

registerElementDefinition({
  type: 'accountsDropdown',
  componentType: AccountsDropdownComponent,
  isListed: true,
  name: 'Accounts Dropdown',
  width: { mode: 'value', value: DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: MIN_SIZE, fixed: true },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  defaults: getDefaults(),
  initial: getInitial(),
  propsBuilder: AccountsDropdownPropsBuilder,
  isCustomerPortalOnly: true,
  styleIncludePaths: [
    'style',
    'content.title.align',
    'content.title.iconPosition',
    'content.title.position',
    'content.title.width',
  ],
  metaUiFeatures: { type: 'input' },
  version: 2,
  migrations: {
    2: accountsDropdownV2Migration,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      { name: 'selected', isDefaultDataValueInput: true },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [
      {
        eventType: 'change',
        schema: {
          // default schema
          columns: [
            {
              columnType: 'String',
              dataType: 'String',
              isProfilePicture: false,
              name: 'Name',
            },
            {
              columnType: 'String',
              dataType: 'String',
              isId: true,
              isInternal: true,
              isProfilePicture: false,
              name: '_id',
            },
          ],
          id: '',
          name: 'Account',
        },
      },
    ],
  };
}

type PropsModel = CanvasElementPropertiesWithData & VisibilityElementProps;

export type AccountsDropdownPropsModel = PropsModel & {
  content: {
    title: ElementLabelPropsWithPositionAndWidth;
  };
  style: {
    fontSize: ValueWithUnitOfMeasure;
    fontFamily: string;
    lineHeight: ValueWithUnitOfMeasure;
    general: {
      title: PropertiesConfigInputColorModel;
      accent: PropertiesConfigInputColorModel;
    };
    labelBlock: ElementLabelStyleProps;
    input: {
      color: PropertiesConfigInputColorModel;
      fill: PropertiesConfigInputColorModel;
      border: PropertiesConfigInputColorModel;
      radius: BorderRadiusValue;
    };
    list: {
      value: PropertiesConfigInputColorModel;
      background: PropertiesConfigInputColorModel;
      border: PropertiesConfigInputColorModel;
      radius: BorderRadiusValue;
    };
  };
};

function getDefaults(): PropsOf<'accountsDropdown'> {
  return {
    content: {
      title: {
        label: 'Account',
        position: 'left',
        align: 'left',
        width: '30%',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
    },
    style: {
      fontSize: '$t.fontSize',
      fontFamily: '$t.fontFamily',
      lineHeight: '$t.lineHeight',
      general: {
        title: { color1: '$t.title', selectorType: 'solid' },
        accent: { color1: '$t.accent', selectorType: 'solid' },
      },
      labelBlock: {
        fontFamily: '$t.fontFamily',
        fontSize: '$t.fontSize',
        fontStyle: [],
        fontWeight: '$t.labelFontWeight',
        iconSize: '16px',
        lineHeight: '$t.lineHeight',
      },
      input: {
        color: { color1: '$t.value', selectorType: 'solid' },
        fill: { color1: '$t.fill', selectorType: 'solid' },
        border: { color1: '$t.outline', selectorType: 'solid' },
        radius: '$t.radius',
      },
      list: {
        value: { color1: '$t.value', selectorType: 'solid' },
        background: { color1: '$t.fill', selectorType: 'solid' },
        border: { color1: '$t.outline', selectorType: 'solid' },
        radius: '$t.radius',
      },
    },
  };
}

function getInitial(): PartialPropsOf<'accountsDropdown'> {
  return {
    content: {
      title: {
        width: '35%',
      },
    },
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('accounts-dropdown', [
    epc.section('content', 'Content', [
      epc.subsections.elementSizeSubsection(
        MIN_SIZE,
        MAX_WIDTH,
        MAX_HEIGHT,
        WIDTH_OPTIONS,
        HEIGHT_OPTIONS,
      ),
      epc.subsections.labelContentSubsection('content'),
      epc.subsections.visibilitySubsection(),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.subsection('style.general', 'General', [
          epc.fields.colorField('style.general.accent', 'Accent', {
            allowGradient: false,
            allowNone: false,
          }),
        ]),

        epc.subsections.subsection('style.input', 'Input', [
          epc.fields.colorField('style.input.color', 'Color', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.input.fill', 'Fill', { allowNone: false }),
          epc.fields.colorField('style.input.border', 'Border', { allowGradient: false }),
          epc.fields.radiusField('style.input.radius'),
        ]),

        epc.subsections.subsection('style.list', 'List', [
          epc.fields.colorField('style.list.value', 'Value', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.list.background', 'Background', { allowNone: false }),
          epc.fields.colorField('style.list.border', 'Border', { allowGradient: false }),
          epc.fields.radiusField('style.list.radius'),
        ]),

        epc.subsections.labelStyleSubsection(
          {
            color: 'style.general.title',
            enabled: 'content.title.enabled',
            fontFamily: 'style.labelBlock.fontFamily',
            fontSize: 'style.labelBlock.fontSize',
            fontStyle: 'style.labelBlock.fontStyle',
            fontWeight: 'style.labelBlock.fontWeight',
            horizontalAlign: 'content.title.align',
            icon: 'content.title.icon',
            iconColor: 'content.title.iconColor',
            iconPosition: 'content.title.iconPosition',
            iconSize: 'style.labelBlock.iconSize',
            lineHeight: 'style.labelBlock.lineHeight',
            position: 'content.title.position',
            width: 'content.title.width',
          },
          {
            excludedPositions: ['right'],
          },
        ),
      ],
      true,
    ),
  ]);
}
